import { useState, useRef, useEffect } from "react";
import { Bell, X, Check, CheckCheck } from "lucide-react";
import { useStore } from "../../store";

export function NotificationsPanel() {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const { notifications, markNotificationRead, markAllRead } = useStore();
  const isRead = (n: { read: boolean; isRead?: boolean }) => n.read || !!n.isRead;
  const unread = notifications.filter(n => !isRead(n)).length;

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  return (
    <div ref={ref} style={{ position: "relative" }}>
      <button
        className="icon-btn"
        onClick={() => setOpen(o => !o)}
        style={{ position: "relative" }}
        aria-label="Notifications"
      >
        <Bell size={18} />
        {unread > 0 && (
          <span style={{
            position: "absolute", top: -4, right: -4,
            background: "var(--cobalt)", color: "white",
            fontSize: 10, fontWeight: 700, borderRadius: "50%",
            width: 16, height: 16, display: "flex", alignItems: "center", justifyContent: "center",
          }}>{unread > 9 ? "9+" : unread}</span>
        )}
      </button>

      {open && (
        <div style={{
          position: "absolute", top: "calc(100% + 8px)", right: 0,
          width: 340, background: "var(--surface-1)",
          border: "1px solid var(--border)", borderRadius: "var(--radius-lg)",
          boxShadow: "var(--shadow-lg)", zIndex: 1000,
          overflow: "hidden",
        }}>
          {/* Header */}
          <div style={{
            display: "flex", alignItems: "center", justifyContent: "space-between",
            padding: "var(--space-3) var(--space-4)",
            borderBottom: "1px solid var(--border)",
          }}>
            <span style={{ fontWeight: 700, fontSize: "var(--text-sm)" }}>
              Notifications {unread > 0 && <span style={{ color: "var(--cobalt)", fontWeight: 800 }}>({unread})</span>}
            </span>
            <div style={{ display: "flex", gap: "var(--space-2)" }}>
              {unread > 0 && (
                <button className="btn btn-xs btn-ghost" onClick={markAllRead} title="Mark all read">
                  <CheckCheck size={13} />
                </button>
              )}
              <button className="icon-btn" onClick={() => setOpen(false)} style={{ padding: 4 }}>
                <X size={14} />
              </button>
            </div>
          </div>

          {/* Notifications list */}
          <div style={{ maxHeight: 360, overflowY: "auto" }}>
            {notifications.length === 0 ? (
              <div style={{ padding: "var(--space-6)", textAlign: "center", color: "var(--fg-muted)", fontSize: "var(--text-sm)" }}>
                No notifications
              </div>
            ) : (
              notifications.map(n => (
                <div
                  key={n.id}
                  onClick={() => markNotificationRead(n.id)}
                  style={{
                    display: "flex", gap: "var(--space-3)", padding: "var(--space-3) var(--space-4)",
                    borderBottom: "1px solid var(--border)",
                    background: isRead(n) ? "transparent" : "var(--cobalt-subtle)",
                    cursor: "pointer",
                    transition: "background 0.15s",
                  }}
                >
                  <div style={{
                    width: 8, height: 8, borderRadius: "50%", flexShrink: 0,
                    background: isRead(n) ? "transparent" : "var(--cobalt)",
                    marginTop: 6,
                  }} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: isRead(n) ? 400 : 600, fontSize: "var(--text-sm)", marginBottom: 2 }}>{n.title}</div>
                    {n.body && <div style={{ fontSize: "var(--text-xs)", color: "var(--fg-muted)", lineHeight: 1.4 }}>{n.body}</div>}
                    <div style={{ fontSize: "var(--text-xs)", color: "var(--ink-40)", marginTop: 4 }}>
                      {new Date(n.createdAt).toLocaleString("en-IN", { hour: "2-digit", minute: "2-digit", month: "short", day: "numeric" })}
                    </div>
                  </div>
                  {!isRead(n) && (
                    <button className="icon-btn" onClick={e => { e.stopPropagation(); markNotificationRead(n.id); }} style={{ padding: 4, alignSelf: "flex-start", flexShrink: 0 }}>
                      <Check size={12} />
                    </button>
                  )}
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}
